<template>
    <div>
        <FormControl
            required
            :label="localizer.translate(TextTokenEnum.BookingForm_Name)"
        >
            <TextInput
                :disabled="!!authCustomer"
                :useBookingFormThemeCssVariables="true"
                :name="RequestBodyKeyEnum.ScheduleMeeting_CustomerName"
                :value="
                    draftMeeting.customerName ?? authCustomer?.getFullName()
                "
                required
                shouldFocusOnMounted
            />
            <input
                v-if="authCustomer"
                type="hidden"
                :name="RequestBodyKeyEnum.ScheduleMeeting_CustomerName"
                :value="authCustomer.getFullName()"
            />
        </FormControl>
    </div>
    <div>
        <FormControl
            required
            :label="localizer.translate(TextTokenEnum.BookingForm_Email)"
        >
            <TextInput
                :disabled="!!authCustomer"
                :useBookingFormThemeCssVariables="true"
                type="email"
                :name="RequestBodyKeyEnum.ScheduleMeeting_CustomerEmail"
                :value="draftMeeting.customerEmail ?? authCustomer?.email"
                required
            />

            <input
                v-if="authCustomer"
                type="hidden"
                :name="RequestBodyKeyEnum.ScheduleMeeting_CustomerEmail"
                :value="authCustomer.email"
            />
        </FormControl>
    </div>
    <template
        v-if="
            recurrenceSettings.isRecurring() &&
            recurrenceSettings.allowCustomNumberOfRecurringMeetings
        "
    >
        <div>
            <FormControl
                required
                :label="
                    localizer.translate(
                        TextTokenEnum.BookingForm_NumberOfBookings,
                    )
                "
            >
                <SelectInput
                    :useBookingFormThemeCssVariables="true"
                    :name="
                        RequestBodyKeyEnum.ScheduleMeeting_NumberOfRecurringMeetingsToSchedule
                    "
                    :value="
                        draftMeeting.numberOfRecurringMeetingsToSchedule ??
                        recurrenceSettings.maximumNumberOfRecurringMeetings
                    "
                    required
                    @update:modelValue="
                        handleNumberOfRecurringMeetingsToScheduleChange($event)
                    "
                >
                    <option
                        v-for="option in recurrenceSettings.getQuantityOptions()"
                        :key="option"
                        :value="option"
                    >
                        {{ option }}
                    </option>
                </SelectInput>
            </FormControl>
        </div>
    </template>
    <template v-else-if="recurrenceSettings.isRecurring()">
        <div>
            <input
                type="hidden"
                :name="
                    RequestBodyKeyEnum.ScheduleMeeting_NumberOfRecurringMeetingsToSchedule
                "
                :value="recurrenceSettings.maximumNumberOfRecurringMeetings"
            />
        </div>
    </template>
    <template v-else>
        <div>
            <input
                type="hidden"
                :name="
                    RequestBodyKeyEnum.ScheduleMeeting_NumberOfRecurringMeetingsToSchedule
                "
                value="1"
            />
        </div>
    </template>
    <template v-if="service.shouldDisplayRequestedSeatCountSelector()">
        <div>
            <FormControl
                required
                label="Number of seats"
            >
                <SelectInput
                    :useBookingFormThemeCssVariables="true"
                    :name="
                        RequestBodyKeyEnum.ScheduleMeeting_NumberOfRequestedSeats
                    "
                    :value="numberOfRequestedSeats"
                    required
                    @update:modelValue="
                        handleNumberOfRequestedSeatsChange($event)
                    "
                >
                    <option
                        v-for="option in service.getRequestedSeatCountOptions()"
                        :key="option"
                        :value="option"
                    >
                        {{ option }}
                    </option>
                </SelectInput>
            </FormControl>
        </div>
    </template>
    <template v-else>
        <div>
            <input
                type="hidden"
                :name="
                    RequestBodyKeyEnum.ScheduleMeeting_NumberOfRequestedSeats
                "
                value="1"
            />
        </div>
    </template>
    <div
        v-if="googleSocialLoginEnabled && !authCustomer"
        class="flex justify-end mt-2"
    >
        <GoogleSocialAuthButton
            :useBookingFormThemeCssVariables="true"
            :url="urlFactory.makeCustomerGoogleOAuthRedirectUrl(currentUrl)"
        >
            {{ localizer.translate(TextTokenEnum.BookingForm_LoginWithGoogle) }}
        </GoogleSocialAuthButton>
    </div>
    <div>
        <FormControl
            :label="localizer.translate(TextTokenEnum.BookingForm_Notes)"
        >
            <TextareaInput
                :useBookingFormThemeCssVariables="true"
                :name="RequestBodyKeyEnum.ScheduleMeeting_Notes"
                rows="3"
                :value="draftMeeting.notes"
            />
        </FormControl>
    </div>
</template>

<script setup lang="ts">
    import { UrlFactory } from '@/Shared/Url/UrlFactory'
    import { type PropType } from 'vue'
    import DraftMeeting from '@/App/Booking/Draft/Entity/DraftMeeting'
    import Customer from '@/App/Customer/Entity/Customer'
    import Service from '@/App/Service/Entity/Service'
    import FormControl from '@/Shared/Component/Forms/FormControl.vue'
    import TextareaInput from '@/Shared/Component/Forms/TextareaInput.vue'
    import TextInput from '@/Shared/Component/Forms/TextInput.vue'
    import GoogleSocialAuthButton from '@/App/User/Component/GoogleSocialAuthButton.vue'
    import RecurrenceSettings from '@/App/Service/Entity/RecurrenceSettings'
    import SelectInput from '@/Shared/Component/Forms/SelectInput.vue'
    import type LocalizerService from '@/Shared/Service/Localization/LocalizerService'
    import { RequestBodyKeyEnum } from '@/Shared/AutoGenerated/RequestFieldEnum'
    import { TextTokenEnum } from '@/Shared/AutoGenerated/LocalizationEnum'

    const emits = defineEmits<{
        onNumberOfRecurringMeetingsToScheduleUpdated: [
            { numberOfRecurringMeetingsToSchedule: number },
        ]
        onNumberOfRequestedSeatsUpdated: [{ numberOfRequestedSeats: number }]
    }>()

    const props = defineProps({
        draftMeeting: {
            type: Object as PropType<DraftMeeting>,
            required: true,
        },
        service: {
            type: Object as PropType<Service>,
            required: true,
        },
        authCustomer: {
            type: Object as PropType<Customer | null>,
            default: null,
        },
        googleSocialLoginEnabled: {
            type: Boolean,
            required: true,
        },
        currentUrl: {
            type: String,
            required: true,
        },
        recurrenceSettings: {
            type: Object as PropType<RecurrenceSettings>,
            required: true,
        },
        numberOfRequestedSeats: {
            type: Number,
            required: true,
        },
        localizer: {
            type: Object as PropType<LocalizerService>,
            required: true,
        },
    })

    const handleNumberOfRecurringMeetingsToScheduleChange = (
        value: string | null,
    ): void => {
        if (!value) {
            return
        }

        emits('onNumberOfRecurringMeetingsToScheduleUpdated', {
            numberOfRecurringMeetingsToSchedule: parseInt(value),
        })
    }

    const handleNumberOfRequestedSeatsChange = (value: string | null): void => {
        if (!value) {
            return
        }

        emits('onNumberOfRequestedSeatsUpdated', {
            numberOfRequestedSeats: parseInt(value),
        })
    }
    const urlFactory = new UrlFactory()
</script>
